import { useEffect, useRef } from "react";
import { motion, useReducedMotion } from "framer-motion";
import confetti from "canvas-confetti";
import { cn } from "@/lib/cn";
import { fmt } from "@/lib/format";
import { medalColor } from "@/lib/theme";
import type { RankingRow, AchievementPodiumRow, MedalItem } from "@/api/types";

export interface AchievementsPodiumProps {
  top3: RankingRow[];
  onSelect?: (user: RankingRow) => void;
}

/** Narrow a polymorphic RankingRow to the achievements shape. The other
 * row types lack `medals`/`total_score`, which this podium needs. */
function isAchievementRow(row: RankingRow): row is AchievementPodiumRow {
  return "total_score" in row && "medals" in row;
}

const RANK_META = {
  1: { label: "Oro", icon: "🏆", tier: "gold" as const, tall: "h-56" },
  2: { label: "Plata", icon: "🥈", tier: "silver" as const, tall: "h-44" },
  3: { label: "Bronce", icon: "🥉", tier: "bronze" as const, tall: "h-36" },
};

const TIER_GRADIENT = {
  gold: "from-[#fbbf24]/25 via-[#fbbf24]/10 to-transparent border-[#fbbf24]/40",
  silver: "from-[#cbd5e1]/20 via-[#cbd5e1]/8 to-transparent border-[#cbd5e1]/35",
  bronze: "from-[#d97706]/25 via-[#d97706]/10 to-transparent border-[#d97706]/35",
};

/** Cinematic podium for top-3 achievement earners. The gold column is
 * taller, lands last with a small bounce, and fires a single confetti
 * burst on first mount (respects prefers-reduced-motion). */
export function AchievementsPodium({ top3, onSelect }: AchievementsPodiumProps) {
  const prefersReducedMotion = useReducedMotion();
  const confettiFired = useRef(false);

  // Fire a small one-shot confetti burst on first mount when the gold
  // pillar is present. Guarded by reduced-motion and a ref so remounts
  // (StrictMode double-invoke, tab switches) don't re-trigger.
  useEffect(() => {
    if (prefersReducedMotion) return;
    if (confettiFired.current) return;
    if (top3.length === 0) return;
    confettiFired.current = true;
    const id = window.setTimeout(() => {
      confetti({
        particleCount: 40,
        spread: 55,
        startVelocity: 35,
        origin: { x: 0.5, y: 0.35 },
        colors: ["#fbbf24", "#c084fc", "#ec4899", "#a78bfa"],
        scalar: 0.85,
        disableForReducedMotion: true,
      });
    }, 650);
    return () => window.clearTimeout(id);
  }, [prefersReducedMotion, top3.length]);

  if (top3.length === 0) return null;

  const first = top3[0];
  const second = top3[1];
  const third = top3[2];

  // Visual order is 2-1-3 but we want the gold pillar to animate LAST
  // so it "lands" on top. Delays encode the stagger.
  return (
    <div className="relative grid grid-cols-3 items-end gap-6 pt-10 pb-4">
      <Pillar
        entry={second}
        rank={2}
        delay={prefersReducedMotion ? 0 : 0.05}
        reduced={prefersReducedMotion ?? false}
        onSelect={onSelect}
      />
      <Pillar
        entry={first}
        rank={1}
        delay={prefersReducedMotion ? 0 : 0.5}
        reduced={prefersReducedMotion ?? false}
        onSelect={onSelect}
      />
      <Pillar
        entry={third}
        rank={3}
        delay={prefersReducedMotion ? 0 : 0.2}
        reduced={prefersReducedMotion ?? false}
        onSelect={onSelect}
      />
    </div>
  );
}

interface PillarProps {
  entry: RankingRow | undefined;
  rank: 1 | 2 | 3;
  delay: number;
  reduced: boolean;
  onSelect?: (user: RankingRow) => void;
}

function Pillar({ entry, rank, delay, reduced, onSelect }: PillarProps) {
  if (!entry) return <div />;
  const meta = RANK_META[rank];
  const isGold = rank === 1;
  const row = isAchievementRow(entry) ? entry : null;
  const username = "username" in entry ? entry.username : "—";
  const score = row?.total_score ?? ("score" in entry ? entry.score : 0);
  const earnedMedals = (row?.medals ?? []).filter((m) => m.unlocked).slice(0, 3);
  const color = medalColor(rank);

  const initial = (rank === 1 ? { y: 80, scale: 0.8, opacity: 0 } : { y: 40, opacity: 0 });
  const animate = { y: 0, scale: 1, opacity: 1 };
  const clickable = Boolean(onSelect);

  return (
    <motion.button
      type="button"
      onClick={onSelect ? () => onSelect(entry) : undefined}
      initial={reduced ? { opacity: 0 } : initial}
      animate={reduced ? { opacity: 1 } : animate}
      transition={
        reduced
          ? { duration: 0.2 }
          : {
              delay,
              type: "spring",
              stiffness: isGold ? 260 : 200,
              damping: isGold ? 14 : 18,
              mass: 0.9,
            }
      }
      className={cn(
        "group relative flex flex-col items-center gap-3 rounded-[22px] border bg-gradient-to-b p-5 pt-10 text-center backdrop-blur-md",
        "transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-1",
        TIER_GRADIENT[meta.tier],
        meta.tall,
        isGold && "shadow-[0_0_60px_-10px_rgba(251,191,36,0.45)]",
        clickable && "cursor-pointer hover:border-white/20",
      )}
      aria-label={`${meta.label}: ${username}, ${fmt(score)} puntos`}
    >
      {/* Floating trophy + rotating glow halo */}
      <motion.div
        aria-hidden
        className="absolute -top-8 left-1/2 -translate-x-1/2"
        animate={reduced ? undefined : { y: [0, -4, 0] }}
        transition={reduced ? undefined : { duration: 2.4, repeat: Infinity, ease: "easeInOut" }}
      >
        <div className="relative flex h-14 w-14 items-center justify-center">
          <motion.div
            className="absolute inset-0 rounded-full opacity-70 blur-md"
            style={{ background: `radial-gradient(circle, ${color}aa 0%, transparent 70%)` }}
            animate={reduced ? undefined : { rotate: 360, scale: [1, 1.1, 1] }}
            transition={
              reduced
                ? undefined
                : { rotate: { duration: 6, repeat: Infinity, ease: "linear" }, scale: { duration: 2.4, repeat: Infinity, ease: "easeInOut" } }
            }
          />
          <div
            className="relative text-3xl drop-shadow-[0_0_8px_rgba(0,0,0,0.5)]"
            style={{ filter: `drop-shadow(0 0 6px ${color})` }}
          >
            {meta.icon}
          </div>
        </div>
      </motion.div>

      {/* Rank chip */}
      <div
        aria-hidden
        className={cn(
          "absolute top-3 left-1/2 -translate-x-1/2 rounded-full px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-[0.18em] text-bg-0",
        )}
        style={{ background: color }}
      >
        {rank}º {meta.label}
      </div>

      {/* Gold shimmer particles */}
      {isGold && !reduced && <GoldParticles />}

      {/* Username + points ticker */}
      <div className="mt-4 flex flex-1 flex-col items-center justify-end gap-1">
        <div className="truncate font-display text-base font-semibold text-text-1">
          {username}
        </div>
        <PointsTicker value={score} reduced={reduced} delay={delay + 0.2} />
        <div className="text-[10px] uppercase tracking-wider text-text-4">Puntos</div>

        {/* Top-3 medals earned */}
        {earnedMedals.length > 0 && (
          <div className="mt-2 flex items-center gap-1.5">
            {earnedMedals.map((m: MedalItem) => (
              <span
                key={m.code}
                title={m.label}
                className="flex h-7 w-7 items-center justify-center rounded-full border border-white/10 bg-white/5 text-sm"
              >
                {m.emoji}
              </span>
            ))}
          </div>
        )}
      </div>
    </motion.button>
  );
}

function PointsTicker({
  value,
  reduced,
  delay,
}: {
  value: number;
  reduced: boolean;
  delay: number;
}) {
  if (reduced) {
    return (
      <div className="tabular font-display text-2xl font-bold text-text-1">
        {fmt(value)}
      </div>
    );
  }
  return (
    <motion.div
      className="tabular font-display text-2xl font-bold text-text-1"
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay, duration: 0.4 }}
    >
      {fmt(value)}
    </motion.div>
  );
}

/** Lightweight CSS-only gold shimmer. Six dots drifting upward; the
 * container is absolutely positioned inside the gold pillar. */
function GoldParticles() {
  const dots = Array.from({ length: 6 }, (_, i) => i);
  return (
    <div aria-hidden className="pointer-events-none absolute inset-0 overflow-hidden rounded-[22px]">
      {dots.map((i) => (
        <motion.span
          key={i}
          className="absolute block h-1 w-1 rounded-full bg-gold-2"
          style={{
            left: `${12 + i * 14}%`,
            bottom: -4,
            boxShadow: "0 0 6px #fbbf24",
          }}
          initial={{ y: 0, opacity: 0 }}
          animate={{ y: -140, opacity: [0, 0.9, 0] }}
          transition={{
            duration: 3 + (i % 3) * 0.6,
            repeat: Infinity,
            delay: i * 0.35,
            ease: "easeOut",
          }}
        />
      ))}
    </div>
  );
}
